import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { useTheme } from '../../contexts/ThemeContext'

export const BusinessRegistrationPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  const registrationOptions = [
    {
      type: 'No Local Presence',
      complexity: 'Low',
      description: 'Sell directly from UK to EU customers',
      requirements: [
        'UK business entity sufficient',
        'VAT registration in destination countries (OSS available)',
        'EU Authorized Representative for product compliance',
        'Customs declarations for each shipment',
      ],
      pros: ['Minimal setup costs', 'No ongoing local admin', 'Quick to start'],
      cons: ['Customs delays for customers', 'Higher shipping costs', 'Limited local presence'],
    },
    {
      type: 'EU Fulfillment Center',
      complexity: 'Medium',
      description: 'Stock inventory in EU warehouse (FBA or 3PL)',
      requirements: [
        'VAT registration in warehouse country',
        'Fiscal representative in some countries',
        'Intrastat reporting if applicable',
        'Product compliance for local market',
      ],
      pros: ['Fast delivery to EU customers', 'No customs for EU orders', 'Competitive with local sellers'],
      cons: ['Inventory investment', 'VAT complexity', 'Multiple registrations'],
    },
    {
      type: 'EU Subsidiary',
      complexity: 'High',
      description: 'Establish local business entity in EU country',
      requirements: [
        'Company registration in chosen country',
        'Local bank account',
        'Local accounting/tax compliance',
        'Potential director residency requirements',
      ],
      pros: ['Full EU business presence', 'Simplified compliance', 'Local market credibility'],
      cons: ['High setup costs (€5,000-20,000)', 'Ongoing admin burden', 'Annual reporting'],
    },
  ]

  const countryInfo = [
    { country: 'Netherlands', entity: 'BV', cost: '€1-2,000', time: '1-2 weeks', notes: 'Popular for UK businesses, English-friendly' },
    { country: 'Ireland', entity: 'Ltd', cost: '€500-1,000', time: '1 week', notes: 'English-speaking, similar legal system' },
    { country: 'Germany', entity: 'GmbH', cost: '€12,500+ capital', time: '4-8 weeks', notes: 'Largest EU market, strict requirements' },
    { country: 'Estonia', entity: 'OÜ', cost: '€190', time: '1 day (e-Residency)', notes: 'Digital-first, minimal capital' },
    { country: 'France', entity: 'SARL/SAS', cost: '€1,000-3,000', time: '2-4 weeks', notes: 'Complex labor laws, large market' },
  ]

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/compliance')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              cursor: 'pointer',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            &larr; Back to Compliance Center
          </button>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Business Registration Options
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Local entity requirements, business licenses, and market entry structures
          </p>
        </div>

        {/* Registration Options */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '32px' }}>
          {registrationOptions.map((option) => (
            <Card key={option.type} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{option.type}</h3>
                  <span style={{
                    padding: '4px 10px',
                    backgroundColor: option.complexity === 'Low' ? '#50C87815' : option.complexity === 'Medium' ? '#F59E0B15' : '#E53E3E15',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: option.complexity === 'Low' ? '#50C878' : option.complexity === 'Medium' ? '#F59E0B' : '#E53E3E',
                  }}>
                    {option.complexity} Complexity
                  </span>
                </div>
                <p style={{ fontSize: '14px', color: colors.textSecondary }}>{option.description}</p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>REQUIREMENTS</h4>
                <ul style={{ margin: 0, paddingLeft: '16px', color: colors.textSecondary, fontSize: '13px', lineHeight: 1.7 }}>
                  {option.requirements.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: 'auto' }}>
                <div style={{ padding: '12px', backgroundColor: '#50C87810', borderRadius: '8px' }}>
                  <h5 style={{ fontSize: '12px', fontWeight: 600, color: '#50C878', marginBottom: '6px' }}>PROS</h5>
                  <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.6 }}>
                    {option.pros.map((pro, idx) => (
                      <li key={idx}>{pro}</li>
                    ))}
                  </ul>
                </div>
                <div style={{ padding: '12px', backgroundColor: '#E53E3E10', borderRadius: '8px' }}>
                  <h5 style={{ fontSize: '12px', fontWeight: 600, color: '#E53E3E', marginBottom: '6px' }}>CONS</h5>
                  <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.6 }}>
                    {option.cons.map((con, idx) => (
                      <li key={idx}>{con}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Country Entity Options */}
        <Card>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: colors.textPrimary }}>
            EU Entity Options by Country
          </h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: colors.textSecondary }}>Country</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: colors.textSecondary }}>Entity Type</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: colors.textSecondary }}>Setup Cost</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: colors.textSecondary }}>Timeline</th>
                <th style={{ textAlign: 'left', padding: '12px', fontSize: '13px', color: colors.textSecondary }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {countryInfo.map((item) => (
                <tr key={item.country} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '14px 12px', fontWeight: 600, color: colors.textPrimary }}>{item.country}</td>
                  <td style={{ padding: '14px 12px', color: colors.primary }}>{item.entity}</td>
                  <td style={{ padding: '14px 12px', color: colors.textSecondary }}>{item.cost}</td>
                  <td style={{ padding: '14px 12px', color: colors.textSecondary }}>{item.time}</td>
                  <td style={{ padding: '14px 12px', color: colors.textSecondary, fontSize: '13px' }}>{item.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </main>
    </div>
  )
}
